/**
 * PSV-1906 — weekly seasons + ascension math.
 *
 * Seasons are DERIVED FROM THE CLOCK (same trick as the daily quests):
 * season N covers the N-th ISO week after SEASON_EPOCH (a Monday
 * 00:00 UTC). Nobody schedules a reset job — a player's seasonal
 * counters live under their season number and simply start over when
 * the number moves on. The per-season LEADER is tracked in a `Season`
 * row (id = season number) that the server keeps up to date as players
 * accrue — the hall of fame is just the last rows of that table.
 */

/** Monday 2026-07-27 00:00 UTC — season 1 started here. */
export const SEASON_EPOCH_MS = Date.parse('2026-07-27T00:00:00Z');
const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

export function seasonNumber(now: number): number {
  return Math.max(1, Math.floor((now - SEASON_EPOCH_MS) / WEEK_MS) + 1);
}

export function seasonEndsAt(season: number): number {
  return SEASON_EPOCH_MS + season * WEEK_MS;
}

/** Ascension mining perk: +5% per ascension, capped at +25%. */
export const ASCENSION_YIELD_PER_LEVEL = 0.05;
export const ASCENSION_YIELD_CAP_LEVELS = 5;

export function ascensionYieldMult(ascensions: number): number {
  return 1 + ASCENSION_YIELD_PER_LEVEL * Math.min(ascensions, ASCENSION_YIELD_CAP_LEVELS);
}

/** Star prefix for pilot names: ★, ★★, ★★★… (compact form past 3). */
export function ascensionStars(ascensions: number): string {
  if (ascensions <= 0) return '';
  if (ascensions <= 3) return '★'.repeat(ascensions);
  return `★x${ascensions}`;
}

/**
 * PSV-1906 head start (PO-approved 2026-07-28): an ascended pilot
 * restarts with bonus gems — veterans skip the T1/T2 stretch they've
 * already mastered; fresh players are untouched.
 */
export const ASCENSION_HEAD_START_PER_STAR = 100;
export const ASCENSION_HEAD_START_CAP = 500;

export function ascensionHeadStart(ascensions: number): number {
  return Math.min(ascensions * ASCENSION_HEAD_START_PER_STAR, ASCENSION_HEAD_START_CAP);
}

/** Ascension milestones that grant reward-only cosmetics. */
export const ASCENSION_COSMETIC_MILESTONES: ReadonlyMap<number, string> = new Map([
  [1, 'skin-ascendant'],
  [3, 'trail-ascendant'],
  [5, 'death-ascendant'],
]);

/** Per-player seasonal counters, persisted as ONE flat JSON string
 * (`Player.SeasonStats`) — season rollover is implicit: a stored season
 * number older than the current one zeroes the counters on first touch. */
export interface SeasonStats {
  season: number;
  gems: number;
  kills: number;
}

export function emptySeasonStats(now: number): SeasonStats {
  return { season: seasonNumber(now), gems: 0, kills: 0 };
}

export function serializeSeasonStats(s: SeasonStats): string {
  return JSON.stringify(s);
}

export function parseSeasonStats(raw: unknown, now: number): SeasonStats {
  const empty = emptySeasonStats(now);
  if (typeof raw !== 'string' || raw === '') return empty;
  try {
    const p = JSON.parse(raw) as Partial<SeasonStats> | null;
    if (p === null || typeof p !== 'object') return empty;
    if (typeof p.season !== 'number' || p.season !== empty.season) return empty; // rollover
    return {
      season: empty.season,
      gems: typeof p.gems === 'number' && Number.isFinite(p.gems) ? p.gems : 0,
      kills: typeof p.kills === 'number' && Number.isFinite(p.kills) ? p.kills : 0,
    };
  } catch {
    return empty;
  }
}
